import React, { useEffect, useMemo, useRef, useState } from 'react';
import DateRangePicker from '../DateRangePicker';
import Badge from '../../../Badge';
import { IDateInput } from '../../../../redux/slices/oldBuilder/date/types';
import { PrimaryButton, SecondaryButton } from '../../../Button';
import * as WidgetContainerUI from './WidgetContainerUI';
import DatePickerTypeList from '../DatePickerTypeList';
import { IDateRangePickerHandle } from '../DateRangePicker/DateRangePicker';
import { GraphQLDate } from '../../../../utils/types';
import { DatePickerType } from '../types';
import {
  IDatePickerFieldsFragment,
  TimeUnit,
} from '../../../../utils/apollo/graphqlSchema';
import { UnreachableCaseError } from 'ts-essentials';
import DatePickerHeader from '../DatePickerHeader';
import useComputeAbsoluteTimePeriod from '../hooks/useComputeAbsoluteTimePeriod';
import { MaybeNil } from '../../../../utils/interfaces';
import { datePickerFromDateInput } from '../utils';
import { freeze } from '@reduxjs/toolkit';
import useSyndicateTimezoneLabel from '../../../../utils/hooks/useSyndicateTimezoneLabel';

interface IProps {
  schemaId: MaybeNil<string>;
  dateInput: IDateInput;
  onConfirm: (newDateInput: IDateInput) => void;
  endDate: GraphQLDate;
}

const createDefaultDatePicker = (
  type: DatePickerType,
  dateInput: IDateInput,
): IDatePickerFieldsFragment => {
  if (type === 'DatePickerFixed') {
    return freeze<IDatePickerFieldsFragment>({
      __typename: 'DatePickerFixed',
      start: dateInput.startDate,
      end: dateInput.endDate,
    });
  } else if (type === 'DatePickerLast') {
    return freeze<IDatePickerFieldsFragment>({
      __typename: 'DatePickerLast',
      unit: TimeUnit.Day,
      period: 7,
    });
  } else if (type === 'DatePickerLastFull') {
    return freeze<IDatePickerFieldsFragment>({
      __typename: 'DatePickerLastFull',
      unit: TimeUnit.Week,
      period: 1,
    });
  } else if (type === 'DatePickerSince') {
    return freeze<IDatePickerFieldsFragment>({
      __typename: 'DatePickerSince',
      start: dateInput.startDate,
    });
  } else if (type === 'DatePickerStartOf') {
    return freeze<IDatePickerFieldsFragment>({
      __typename: 'DatePickerStartOf',
      unit: TimeUnit.Week,
    });
  } else {
    throw new UnreachableCaseError(type);
  }
};

export default ({ onConfirm, dateInput, schemaId, endDate }: IProps) => {
  const shortTimeZone = useSyndicateTimezoneLabel();

  const { computeAtpLoading, computeAbsoluteTimePeriod } =
    useComputeAbsoluteTimePeriod(schemaId);

  const [draftDateInput, setDraftDateInput] = useState(dateInput);

  const datePicker = useMemo(
    () => datePickerFromDateInput(draftDateInput),
    [draftDateInput],
  );

  const dateRangeRef = useRef<IDateRangePickerHandle | null>(null);

  const handleDateInputChange = (newDateInput: IDateInput) => {
    setDraftDateInput(newDateInput);
    dateRangeRef.current?.reset();
  };

  const handleReset = () => {
    handleDateInputChange(dateInput);
  };

  const handleDatePickerChange = async (
    newDatePicker: IDatePickerFieldsFragment,
  ) => {
    handleDateInputChange(
      await computeAbsoluteTimePeriod(newDatePicker, endDate),
    );
  };

  const handleDatePickerTypeChange = async (
    newDateRangeType: DatePickerType,
  ) => {
    const datePicker = createDefaultDatePicker(
      newDateRangeType,
      draftDateInput,
    );
    await handleDatePickerChange(datePicker);
  };

  // Update the absolute date range (startDate, endDate in dateInput) from the relative date range
  // (dateInput.datePicker) on first display
  // Can't use useEffect because want this code run only once, when component mounts for the first time
  const mountEffectFunc = useRef(async () => {
    await handleDatePickerChange(datePicker);
  });
  useEffect(() => {
    mountEffectFunc.current();
  }, []);

  return (
    <>
      <WidgetContainerUI.Container $disabled={computeAtpLoading}>
        <WidgetContainerUI.StartDateShortcutContainer>
          <DatePickerTypeList
            value={datePicker.__typename}
            onChange={handleDatePickerTypeChange}
          />
        </WidgetContainerUI.StartDateShortcutContainer>
        <div>
          <DatePickerHeader
            datePicker={datePicker}
            endDate={endDate}
            onChange={handleDatePickerChange}
          />
          <DateRangePicker
            ref={dateRangeRef}
            dateInput={draftDateInput}
            onChange={handleDatePickerChange}
          />
        </div>
      </WidgetContainerUI.Container>
      <WidgetContainerUI.Footer>
        <Badge color="grey" size="primary">
          {shortTimeZone}
        </Badge>
        <WidgetContainerUI.Space>
          <SecondaryButton
            type="button"
            onClick={handleReset}
            trackContext="date picker"
            trackAction="reset"
          >
            Reset
          </SecondaryButton>
          <PrimaryButton
            type="button"
            onClick={() => onConfirm(draftDateInput)}
            disabled={computeAtpLoading}
            trackContext="date picker"
            trackAction="apply"
          >
            Apply
          </PrimaryButton>
        </WidgetContainerUI.Space>
      </WidgetContainerUI.Footer>
    </>
  );
};
